// 회생채권 변제계획 화면 공통 상수 (2026-09-02 HSJ(Claude))

// 변제율 표시값 — 법원 인가 회생계획안 변제율 공지 기준(공지·FAQ 등 대외 문서 = 15.15%).
// 테이블 저장값(repaymentRate)은 셀러별 「변제예정액 합 ÷ 시인액 합」이라 원 단위 절사로 15.1439~15.1461% 편차가 있어
// 화면·엑셀 표시는 공지값으로 통일한다(2026-09-02 재무팀 요청). 계산·검산은 저장값 그대로 사용(표시만 통일).
export const REHABILITATION_REPAYMENT_RATE_LABEL = '15.15%'

// 정산대금 (주문단위) 목록 서버비 차감 행(rowSeq 0)의 상품명 표시 문구 — 재무팀 변경요청 v1.0.1 (2) 「서버비 차감 (셀러 단위)」.
// 데이터(product_name)는 '서버비 차감'이며 화면·엑셀 표시에서만 이 문구로 치환 (2026-09-04 사용자 결정, 재적재 없이 반영)
export const REHABILITATION_SERVER_FEE_ROW_LABEL = '서버비 차감 (셀러 단위)'

// ---------------------------------------------------------------------------------------------------------------------
// 용어 설명(툴팁) — 재무팀 「하이버 어드민 툴팁 용어 리스트 v1.0 (2026-09-03, 윤원국·검수 MD팀 김혜서)」 문구 그대로.
// 규칙: 항목명 기준 문구 1개(같은 항목명은 화면 어디서나 동일), 괄호 병기 항목은 별개 항목. 개정 시 이 상수만 교체(문안 임의 수정 금지).
// 원문에서 두세 항목을 한 문장으로 쓴 곳(주문일시/구매확정일시, 주문번호/주문상세번호, 일자/구분/금액)은 헤더별 툴팁에 맞춰 의미 유지하며 분할.
// 목록 컬럼 「정산확정금액 (B)」·「변제 예정 금액 (E)」는 상단과 이름이 같아 조회 키를 (B)/(E) 로 구분(화면 표기에는 기호 없음). 목록 B·D·E 존치는 운영팀 협의 대기.
// ---------------------------------------------------------------------------------------------------------------------
export interface RehabilitationTerm {
  term: string // 항목명(조회 키)
  description: string
}

export const REHABILITATION_GLOSSARY_VERSION = 'v1.0 (2026-09-03)'

export const REHABILITATION_GLOSSARY: RehabilitationTerm[] = [
  {
    term: '시인된 총 채권액',
    description:
      '회생절차에서는 법원이 정한 조사 기간에 채권 금액을 확인합니다. 이 과정에서 이의 없이 인정된 판매자님의 채권 금액입니다. 변제 예정 금액은 이 금액을 기준으로 계산됩니다.',
  },
  { term: '변제율', description: '회생계획안에서 정한 변제 비율(15.15%)입니다. 모든 채권자에게 같은 비율이 적용됩니다.' },
  {
    term: '변제 예정 금액',
    description:
      '시인된 총 채권액에 변제율을 적용해 계산한, 판매자님께 지급될 예정 금액입니다. 법원의 인가결정을 조건으로 하며, 인가 후 법원 허가를 받아 정해지는 변제기일(늦어도 인가일로부터 약 50일 이내)에 지급됩니다.',
  },
  { term: '시인된 총 채권액 합계', description: '정산대금과 광고선수금, 두 가지 시인된 총 채권액을 더한 금액입니다.' },
  {
    term: '해당 사항 없음',
    description: '이 계정으로는 조회되는 회생채권 내역이 없습니다. 계정을 여러 개 쓰고 계시다면 다른 계정으로도 확인해 주세요.',
  },
  {
    term: '채권자명',
    description:
      '회생채권자 목록에 올라 있는 판매자님의 이름입니다. 법인은 상호와 대표자 성명, 개인사업자는 성명과 상호가 함께 표시되어 어드민의 셀러명과 다르게 보일 수 있습니다.',
  },
  {
    term: '사업자번호',
    description:
      '채권자를 구분하는 기준입니다. 같은 사업자번호로 등록된 셀러 계정은 하나의 채권자로 묶여, 어느 계정으로 로그인하셔도 같은 금액이 표시됩니다.',
  },
  {
    term: '시인된 총 채권액(정산대금)',
    description:
      '회생절차가 시작되기 전에 판매하셨지만 아직 정산받지 못한 금액 가운데, 채권 조사에서 인정된 금액입니다. 정산확정금액에서 서버비를 뺀 금액과 같습니다.',
  },
  {
    term: '시인된 총 채권액(광고선수금)',
    description:
      '비즈센터에 유상으로 충전하신 광고비 가운데 쓰지 않고 남아 있던 금액에 대해, 채권 조사에서 인정된 금액입니다. 충전 합계에서 소진·환불 합계를 뺀 금액과 같습니다.',
  },
  {
    term: '주문 단위 근거 조회',
    description: '정산대금이 어떤 주문에서 나온 금액인지 주문별로 확인하실 수 있는 「정산대금 (주문단위)」 화면으로 이동합니다.',
  },
  {
    term: '광고 단위 근거 조회',
    description:
      '광고선수금이 어떻게 계산되었는지 날짜별 충전·소진·환불 내역으로 확인하실 수 있는 「광고선수금 (계정단위)」 화면으로 이동합니다.',
  },
  { term: '정산확정금액', description: '회생채권을 신고할 때 기준이 된 정산 예정 금액입니다.' },
  {
    term: '서버비',
    description: '하이버 서버 이용료입니다. 정산확정금액에서 서버비를 뺀 금액이 채권 조사에서 인정된 시인된 총 채권액이 됩니다.',
  },
  {
    term: '변제 예정 금액(정산대금)',
    description:
      '정산대금 부분에 해당하는 변제 예정 금액입니다. 광고선수금도 있으시다면, 두 가지를 합한 전체 금액은 「변제금 합계 (사업자단위)」 화면에서 확인하실 수 있습니다.',
  },
  { term: '주문일시', description: '주문이 들어온 시각입니다.' },
  { term: '구매확정일시', description: "구매확정이 된 시각입니다. 아직 구매확정 전인 주문은 '-'로 표시됩니다." },
  { term: '주문번호', description: '하이버 주문번호입니다.' },
  { term: '주문상세번호', description: '주문 안의 상품(옵션) 단위 번호입니다. 채권액은 주문상세 단위로 집계됩니다.' },
  {
    term: '정산확정금액 (B)',
    description: '이 주문상세의 회생채권 신고 당시 정산 예정 금액입니다. 목록의 금액을 모두 더하면 상단의 정산확정금액이 됩니다.',
  },
  {
    term: '변제 예정 금액 (E)',
    description:
      '이 주문상세의 정산확정금액에 변제율을 적용한 금액입니다. 서버비는 주문이 아니라 셀러 단위로 차감되므로, 이 열의 금액을 모두 더한 값은 상단의 변제 예정 금액(정산대금)과 서버비에 해당하는 만큼 차이가 있습니다.',
  },
  { term: '충전(유상) 합계', description: '비즈센터에 현금으로 충전하신 광고비의 합계와 충전 횟수입니다.' },
  {
    term: '소진(일계) 합계',
    description:
      '광고 집행으로 사용된 유상 충전분의 합계입니다. 광고 한 건 한 건이 아니라 하루 단위로 합산되어 기록되며, 괄호 안 숫자는 집계된 일수입니다.',
  },
  { term: '환불 합계', description: '충전 후 환불받으신 광고비의 합계와 횟수입니다.' },
  {
    term: '변제 예정 금액(광고선수금)',
    description:
      '광고선수금 부분에 해당하는 변제 예정 금액입니다. 정산대금도 있으시다면, 두 가지를 합한 전체 금액은 「변제금 합계 (사업자단위)」 화면에서 확인하실 수 있습니다.',
  },
  { term: '일자', description: '광고비의 충전·소진·환불이 있었던 날짜입니다.' },
  { term: '구분', description: '충전·소진·환불의 종류입니다.' },
  { term: '금액', description: '충전·소진·환불 금액입니다. 충전은 +, 소진과 환불은 −로 표시됩니다.' },
]

// 항목명(또는 조회 키) → 설명. 없으면 빈 문자열(툴팁 미노출)
export function rehabilitationTermDescription(term: string): string {
  const found = REHABILITATION_GLOSSARY.find((item) => item.term === term)
  return found ? found.description : ''
}

// 「해당 사항 없음」 빈 화면 아래 안내문 (툴팁 대상이 아니라 문장으로 표시) — 정산대금·광고선수금 화면
export const REHABILITATION_NO_DATA_NOTE = rehabilitationTermDescription('해당 사항 없음')
// 변제금 합계(사업자 단위)·변제금 수령계좌 조회/등록 화면 전용 안내문 — 2026-09-08 사용자 요청 문안
export const REHABILITATION_NO_DATA_NOTE_ACCOUNT = '이 계정에 등록된 사업자로 조회되는 회생채권 내역이 없습니다.'

// ---------------------------------------------------------------------------------------------------------------------
// FAQ — 재무팀 「어드민_FAQ_인가반영판_20261015.md」(2026-10-15, 인가결정 반영) 게시 문안 그대로. 문항 번호는 원문 그대로(Q2~Q4 집회 문항 삭제로 Q1 다음 Q5).
// 생성: docs 20260714_회생채권변제현황/src/gen_faq_constants_20261015.py — 문안 임의 수정 금지, 개정 시 md 교체 후 재생성. v1.5.2(2026-09-09)는 이 판으로 대체.
// ---------------------------------------------------------------------------------------------------------------------
export interface RehabilitationFaqItem {
  no: string // 문항 번호 (Q1, Q7-1 …)
  question: string
  answer: string // 줄바꿈 포함 (화면에서 pre-line)
}

export const REHABILITATION_FAQ_VERSION = '인가 반영판 (2026-10-15)'

// 상단 안내문 (FAQ 원문 머리글)
export const REHABILITATION_FAQ_INTRO =
  '주식회사 하이버의 회생절차와 관련하여 판매자 여러분께서 자주 문의하시는 내용을 정리했습니다. (2026년 10월 15일 기준이며, 절차 진행에 따라 갱신됩니다.)'

// 하단 문의 안내 (FAQ 원문 맺음글)
export const REHABILITATION_FAQ_OUTRO = '위 내용 외에 궁금하신 사항은 카카오톡 채널 @하이버셀러로 문의해 주시기 바랍니다.'

export const REHABILITATION_FAQ_ITEMS: RehabilitationFaqItem[] = [
  {
    no: 'Q1',
    question: '지금 어떤 단계인가요?',
    answer:
      '2026년 10월 14일 관계인집회에서 회생계획안이 가결되었고, 같은 날 서울회생법원이 회생계획을 승인(인가)했습니다.\n\n앞으로의 순서는 이렇습니다.\n\n① 법원의 허가를 받아 변제기일을 정합니다.\n② 그 날짜까지 승인된 회생계획에 따라 변제금이 지급됩니다.\n\n판매자님께서는 변제금을 받으실 계좌를 2026년 10월 27일(화)까지 등록해 주시기 바랍니다(Q10).',
  },
  {
    no: 'Q5',
    question: '변제 금액은 얼마인가요?',
    answer:
      '회생계획에 따라, 받지 못하신 대금(원금과 회생절차 시작 전까지의 이자)의 15.15%를 현금으로 변제합니다. 나누어 드리지 않고 정해진 날짜에 한 번에 지급됩니다.\n\n이 금액은 인수예정자와의 투자계약으로 마련된 변제 재원을 법원 절차에 따라 채권액에 비례해 나눈 결과이며, 법원의 승인을 받았습니다.',
  },
  {
    no: 'Q6',
    question: '현금으로 변제되지 않는 나머지 금액은 어떻게 되나요?',
    answer:
      '회생계획에 따라, 현금으로 변제되는 15.15%를 제외한 나머지 부분은 회사가 발행하는 신주로 전환되어 회생채권으로서는 소멸하며, 발행된 주식은 전부 무상 소각됩니다. 법원이 회생계획을 승인하였으므로, 이 부분은 더 이상 회사에 청구하실 수 없습니다. 판매자님께서 주식과 관련하여 하셔야 할 절차는 없습니다.\n\n판매자 여러분께 전액을 돌려드리지 못하게 된 점 깊이 사과드립니다. 남은 절차를 지체 없이 진행하여 최대한 빠르게 지급해 드리겠습니다.',
  },
  {
    no: 'Q7',
    question: '제가 받을 금액은 어디서 확인하나요?',
    answer:
      '하이버 입점판매자이신 경우 판매자 어드민 「회생채권 변제계획」 메뉴에서 확인하실 수 있습니다. 금액 화면은 세 개입니다.\n\n- 「변제금 합계 (사업자단위)」 — 판매자님이 받으실 전체 금액입니다. 먼저 이 화면을 봐 주세요.\n- 「정산대금 (주문단위)」 — 그중 정산대금 부분을 주문별로 보여줍니다.\n- 「광고선수금 (계정단위)」 — 그중 광고선수금 부분을 날짜별로 보여줍니다.\n\n표시되는 금액은 법원이 승인한 회생계획에 따른 변제 예정 금액입니다. 판매자 계정을 여러 개 쓰시는 경우에도 어느 계정으로 로그인하시든 같은 금액이 표시됩니다.',
  },
  {
    no: 'Q7-1',
    question: '조회했더니 "해당 사항 없음" 또는 0원으로 나옵니다.',
    answer:
      '「정산대금」과 「광고선수금」 화면은 각각 그 항목만 보여줍니다. 예를 들어 광고비를 충전해 쓰신 적이 없다면 「광고선수금」 화면은 0원으로 나오는 것이 정상입니다. 전체 금액은 「변제금 합계 (사업자단위)」 화면에서 확인해 주세요.\n\n「변제금 합계」 화면에서도 0원이거나 "해당 사항 없음"으로 나오는 경우에는 카카오톡 채널 @하이버셀러로 판매자 ID와 사업자등록번호를 알려주시면 확인해 드리겠습니다.',
  },
  {
    no: 'Q7-2',
    question: '"시인된 총 채권액"이 무엇인가요?',
    answer:
      '회생절차가 시작되면 회사가 채권자 목록을 법원에 내고, 채권자분들도 받을 금액을 신고합니다. 그 다음 법원이 정한 조사 기간에 금액을 확인하는데, 이 과정에서 이의 없이 인정된 금액을 "시인된 채권액"이라고 합니다.\n\n판매자님의 시인된 총 채권액은 정산대금과 광고선수금을 합한 금액이며, 변제 예정 금액은 여기에 15.15%를 적용해 계산됩니다.',
  },
  {
    no: 'Q7-3',
    question: '화면의 금액이 제가 알고 있는 것과 다릅니다. 고칠 수 있나요?',
    answer:
      '화면의 금액은 회생절차 초반의 채권 신고와 조사 절차를 거쳐 정해진 것이며, 법원이 승인한 회생계획에 그대로 반영되어 있습니다. 조사 기간이 끝난 뒤에는 원칙적으로 금액을 바꿀 수 없습니다.\n\n어떤 부분이 다른지 확인을 원하시면, 카카오톡 채널 @하이버셀러로 판매자 ID·사업자등록번호와 함께 다른 부분을 알려주시기 바랍니다.',
  },
  {
    no: 'Q8',
    question: '그동안의 이자나 지연손해금은 받을 수 있나요?',
    answer:
      '회생절차 개시결정일 전날(2025년 9월 28일)까지 발생한 이자는 원금과 함께 변제 대상에 포함되어 똑같이 15.15%가 지급됩니다. 그 이후의 이자는 회생계획에 따라 면제됩니다.',
  },
  {
    no: 'Q9',
    question: '변제금은 언제 받게 되나요?',
    answer:
      '법원의 허가를 받아 변제기일을 정하게 되며, 정해지는 대로 공지사항에 공지드리겠습니다. 변제는 늦어도 승인일로부터 약 50일 이내에 이루어집니다.',
  },
  {
    no: 'Q10',
    question: '변제금은 어떻게 받게 되나요? 지금 할 일이 있나요?',
    answer:
      '판매자 어드민 「회생채권 변제계획」 → 「변제금 수령계좌 조회/등록」 화면에서 수령계좌를 이미 등록하셨다면 따로 하실 일이 없습니다. 등록하신 계좌로 지급됩니다.\n\n아직 등록하지 않으셨다면 2026년 10월 27일(화)까지 같은 화면에서 등록해 주시기 바랍니다. 현재 등록된 정산계좌를 그대로 선택하시거나 다른 계좌를 입력하실 수 있으며, 계좌의 예금주가 채권자 본인(대표자 성명 또는 상호)과 일치하는 경우에 등록됩니다.',
  },
  {
    no: 'Q10-1',
    question: '계좌를 입력했는데 등록이 되지 않습니다.',
    answer:
      '입력하신 계좌의 예금주가 채권자 본인(대표자 성명 또는 상호)과 다른 경우에는 등록되지 않습니다. 가족·직원·다른 회사 명의 계좌는 등록하실 수 없으니, 본인 명의 계좌를 입력해 주시기 바랍니다.\n\n본인 명의 계좌인데도 불일치로 나오는 경우에는 은행에 등록된 예금주명 표기가 채권자명과 조금 다른 것일 수 있습니다. 회사에서 확인하고 있으니 영업일 기준 1~2일 후 같은 화면에서 다시 조회해 주시기 바랍니다.\n\n위임·상속·합병 등의 사정으로 본인 명의 계좌를 등록하실 수 없는 경우에는, 공지사항에 안내된 서류를 2026년 10월 27일(화)까지 rm@brandi.co.kr로 보내 주시기 바랍니다.',
  },
  {
    no: 'Q10-2',
    question: '등록한 계좌를 바꿀 수 있나요?',
    answer:
      '한 번 등록하신 수령계좌는 변제 완료 시까지 변경하실 수 없습니다. 등록 전에 계좌를 다시 한 번 확인해 주시기 바랍니다.\n\n수령계좌는 정산계좌와 별개로 관리됩니다. 정산계좌를 바꾸셔도 수령계좌는 바뀌지 않고, 수령계좌를 등록하셔도 정산에는 영향이 없습니다. 부득이하게 변경이 필요하신 경우 카카오톡 채널 @하이버셀러로 문의해 주시기 바랍니다.',
  },
  {
    no: 'Q10-3',
    question: '기간 안에 등록하지 못하면 어떻게 되나요?',
    answer:
      '기간까지 수령계좌 등록 또는 서류 제출이 완료되지 않아 지급할 계좌가 확인되지 않은 경우, 해당 채권자에 대한 변제는 보류되며, 이후 법원 절차에 따라 변제금이 공탁될 수 있습니다. 이 경우 회생계획에 따라 지연에 따른 이자는 발생하지 않습니다.',
  },
  {
    no: 'Q11',
    question: '지금 다시 판매를 시작하면, 새로 발생하는 판매대금도 줄어드나요?',
    answer:
      '아닙니다. 회생계획의 변제 대상은 회생절차가 시작되기 전에 발생한 대금입니다. 다시 판매를 시작해 새로 발생하는 대금은 과거 대금과 분리되어 전액 정상 정산되며, 과거 대금과 상계되지 않습니다.\n\n또한 현재 판매대금은 결제대행사(PG)를 통해 판매자님 계좌로 직접 정산되는 구조로 운영되고 있어, 회사의 회생절차와 무관하게 안전하게 지급됩니다.',
  },
  {
    no: 'Q12',
    question: '회사는 계속 운영되나요?',
    answer:
      '회사가 없어지는 것이 아니라, 인수예정자의 투자를 통해 회사를 정상화하는 절차입니다. 플랫폼 운영은 계속되며, 판매대금 정산도 회생절차와 별개로 정상적으로 진행되고 있습니다. 남은 절차도 법원의 관리·감독 아래 성실하고 투명하게 이행하겠습니다.',
  },
  {
    no: 'Q13',
    question: '인수예정자는 어디인가요?',
    answer:
      '주식회사 바이브먼트입니다. 당사는 2026년 5월 조건부 투자계약을 체결하였으며, 법원의 승인이 있었고, 인수대금 납입과 신주 효력 발생으로 인수 절차가 마무리됩니다.',
  },
  {
    no: 'Q14',
    question: '세무 처리에 쓸 서류를 받을 수 있나요?',
    answer:
      '회생계획에 따라 변제되는 금액과 소멸된 금액을 확인하실 수 있는 서류를 준비하고 있으며, 제공 시기와 방법은 준비되는 대로 공지드리겠습니다. 그 전에 법원의 승인 결정문 사본이 필요하시면 카카오톡 채널 @하이버셀러로 요청해 주시기 바랍니다.',
  },
]
